/* eslint-disable no-undef */
import {
  createAsyncThunk,
  createEntityAdapter,
  createSlice,
} from "@reduxjs/toolkit";
import { enqueueSnackbar } from "notistack"; // Assuming you are using notistack for notifications

import request from "../../utils/request";

// Create entity adapter
const resumeAdapter = createEntityAdapter();

const initialState = resumeAdapter.getInitialState({
  isLoading: false,
  error: null,
  totalResumes: 0,
  resumeData: {},
});

// Async thunk for fetching all resumes
export const getAllResumes = createAsyncThunk(
  "resume/getAllResumes",
  async (
    { page = 1, query = "", filters = {}, job_post_id, take = 10 },
    { rejectWithValue }
  ) => {
    try {
      const response = await request({
        url: "/results/by-job",
        method: "POST",
        data: { job_post_id, skip: page, take, query, ...filters },
      });

      return {
        page,
        data: response?.data?.data || [],
        count: response?.data?.count || 0,
      };
    } catch (error) {
      enqueueSnackbar(error?.message, {
        autoHideDuration: 3000,
        variant: "error",
      });
      return rejectWithValue(error?.message);
    }
  }
);

// Async thunk for getting a single resume
export const getSingleResume = createAsyncThunk(
  "resume/getSingleResume",
  async (id, { rejectWithValue }) => {
    try {
      const response = await request({
        url: `/results/by-id`,
        method: "GET",
        params: { result_id: id },
      });

      return response.data;
    } catch (error) {
      enqueueSnackbar(error?.message, {
        autoHideDuration: 3000,
        variant: "error",
      });
      return rejectWithValue(error?.message);
    }
  }
);

// Async thunk for fetching reject resume
export const rejectResume = createAsyncThunk(
  "resume/rejectResume",
  async (
    { ids, job_post_id, filters = {}, query = "" },
    { rejectWithValue }
  ) => {
    try {
      let payload = { ids, job_post_id };

      if (ids === "all") {
        let isFilterApplied =
          filters && Object.keys(filters).length > 0 ? true : false;

        if (isFilterApplied) {
          payload = { ...payload, isFilterApplied, ...filters };
        }

        if (query) {
          payload = { ...payload, isFilterApplied: true, ...filters, query };
        }
      }

      const response = await request({
        url: "/results/reject",
        method: "PUT",
        data: payload,
      });

      enqueueSnackbar(response?.message, { variant: "success" });

      console.log("RESPONSE REJECT RESPONSE", response);

      return ids === "all" ? [] : response?.data;
    } catch (error) {
      enqueueSnackbar(error?.message, {
        autoHideDuration: 3000,
        variant: "error",
      });
      return rejectWithValue(error?.message);
    }
  }
);

// Async thunk for fetching delete resume
export const deleteResume = createAsyncThunk(
  "resume/deleteResume",
  async (
    { ids, job_post_id, page, filters = {}, query = "" },
    { rejectWithValue }
  ) => {
    try {
      let payload = { ids, job_post_id };

      if (ids === "all") {
        let isFilterApplied =
          filters && Object.keys(filters).length > 0 ? true : false;

        if (isFilterApplied) {
          payload = query
            ? { ...payload, isFilterApplied, ...filters, query }
            : { ...payload, isFilterApplied, ...filters };
        }
      }

      const response = await request({
        url: "/results/delete",
        method: "DELETE",
        data: payload,
      });

      console.log("RESPONSE DELETE RESUME", response);

      return { ids: ids === "all" ? [] : ids, page };
    } catch (error) {
      enqueueSnackbar(error?.message, {
        autoHideDuration: 3000,
        variant: "error",
      });
      return rejectWithValue(error?.message);
    }
  }
);

// Async thunk for Share result
export const shareResult = createAsyncThunk(
  "result/shareResult",
  async (data, { rejectWithValue }) => {
    try {
      const response = await request({
        url: "/results/share",
        method: "POST",
        data: data,
      });

      if (response?.statusCode === 200) {
        if (typeof cb === "function") {
          cb(response?.data);
        }
      }

      return response.data;
    } catch (error) {
      enqueueSnackbar(error?.message, {
        autoHideDuration: 3000,
        variant: "error",
      });
      return rejectWithValue(error?.message);
    }
  }
);

// Async thunk for Share result
export const removeSharedAccess = createAsyncThunk(
  "result/removeSharedAccess",
  async (data, { rejectWithValue }) => {
    try {
      const response = await request({
        url: "/results/remove-share-access",
        method: "POST",
        data: data,
      });

      if (response?.statusCode === 200) {
        if (typeof cb === "function") {
          cb(response?.data);
        }
      }

      return response.data;
    } catch (error) {
      enqueueSnackbar(error?.message, {
        autoHideDuration: 3000,
        variant: "error",
      });
      return rejectWithValue(error?.message);
    }
  }
);

// Async thunk for getShareResultByResumeId
export const getShareResultByResumeId = createAsyncThunk(
  "result/getShareResultByResumeId",
  async (data, { rejectWithValue }) => {
    try {
      const response = await request({
        url: "/results/get-share-result-by-id",
        method: "POST",
        data: data,
      });

      if (response?.statusCode === 200) {
        if (typeof cb === "function") {
          cb(response?.data);
        }
      }

      return response.data;
    } catch (error) {
      enqueueSnackbar(error?.message, {
        autoHideDuration: 3000,
        variant: "error",
      });
      return rejectWithValue(error?.message);
    }
  }
);

// Async thunk for fetching invite candidates for interview
export const inviteCandidatesForInterview = createAsyncThunk(
  "resume/inviteCandidatesForInterview",
  async (
    {
      assessment_id,
      company_id,
      resume_ids,
      job_post_id,
      languages,
      filters = {},
      query = "",
      passing_criteria,
    },
    { getState, rejectWithValue }
  ) => {
    try {
      const state = getState();
      const candidates =
        resume_ids === "all"
          ? resume_ids
          : resume_ids?.map((id) => {
              const resume = selectResumeById(state, id);

              return {
                candidate_id: resume?.candidate_id,
                resume_score_id: resume?.id,
              };
            });

      let payload = {
        assessment_id,
        company_id,
        candidates,
        job_post_id,
        languages,
        passing_criteria,
      };

      if (candidates === "all") {
        let isFilterApplied =
          filters && Object.keys(filters).length > 0 ? true : false;

        if (isFilterApplied) {
          payload = query
            ? { ...payload, isFilterApplied, ...filters, query }
            : { ...payload, isFilterApplied, ...filters };
        }
      }

      const response = await request({
        url: "/interview/create-by-job",
        method: "POST",
        data: payload,
      });

      enqueueSnackbar(response?.message || "Invite sent", {
        variant: "success",
      });

      return { candidates: candidates === "all" ? [] : candidates };
    } catch (error) {
      enqueueSnackbar(error?.message, {
        autoHideDuration: 3000,
        variant: "error",
      });
      return rejectWithValue(error?.message);
    }
  }
);

// Async thunk for uploading pdfs in bulk
export const uploadPdfsInBulk = createAsyncThunk(
  "resume/uploadPdfsInBulk",
  async ({ data, cb }, { rejectWithValue }) => {
    try {
      const response = await request({
        url: `/upload/pdfs`,
        method: "POST",
        data: data,
      });

      enqueueSnackbar(response?.message, { variant: "success" });
      if (response?.statusCode === 200) {
        if (typeof cb === "function") {
          cb(response?.data);
        }
      }

      return response?.data;
    } catch (error) {
      enqueueSnackbar(error?.message, {
        autoHideDuration: 3000,
        variant: "error",
      });
      return rejectWithValue(error?.message);
    }
  }
);

// Async thunk for uploading csv in bulk
export const uploadCsvInBulk = createAsyncThunk(
  "resume/uploadCsvInBulk",
  async ({ data, cb }, { rejectWithValue }) => {
    try {
      const response = await request({
        url: `/upload/csv`,
        method: "POST",
        data: data,
      });

      enqueueSnackbar(response?.message, { variant: "success" });
      if (response?.statusCode === 200) {
        if (typeof cb === "function") {
          cb(response?.data);
        }
      }

      return response?.data;
    } catch (error) {
      enqueueSnackbar(error?.message, {
        autoHideDuration: 3000,
        variant: "error",
      });
      return rejectWithValue(error?.message);
    }
  }
);

// Async thunk for uploading pdfs in bulk
export const uploadPdfsInBulkForJob = createAsyncThunk(
  "resume/uploadPdfsInBulkForJob",
  async ({ data, cb }, { rejectWithValue }) => {
    try {
      const response = await request({
        url: `/upload/pdfs-on-job`,
        method: "POST",
        data: data,
      });

      enqueueSnackbar(response?.message, { variant: "success" });
      if (response?.statusCode === 200) {
        if (typeof cb === "function") {
          cb(response?.data);
        }
      }

      return response?.data;
    } catch (error) {
      enqueueSnackbar(error?.message, {
        autoHideDuration: 3000,
        variant: "error",
      });
      return rejectWithValue(error?.message);
    }
  }
);

// Async thunk for uploading csv in bulk
export const uploadCsvInBulkForJob = createAsyncThunk(
  "resume/uploadCsvInBulkForJob",
  async ({ data, cb }, { rejectWithValue }) => {
    try {
      const response = await request({
        url: `/upload/csv-on-job`,
        method: "POST",
        data: data,
      });

      enqueueSnackbar(response?.message, { variant: "success" });
      if (response?.statusCode === 200) {
        if (typeof cb === "function") {
          cb(response?.data);
        }
      }

      return response?.data;
    } catch (error) {
      enqueueSnackbar(error?.message, {
        autoHideDuration: 3000,
        variant: "error",
      });
      return rejectWithValue(error?.message);
    }
  }
);

// Slice
const resumeSlice = createSlice({
  name: "resume",
  initialState,
  extraReducers: (builder) => {
    builder
      .addCase(getAllResumes.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getAllResumes.fulfilled, (state, action) => {
        state.isLoading = false;
        state.totalResumes = action.payload.count;
        resumeAdapter.upsertMany(state, action.payload.data || []);
        state.resumeData = {
          ...state.resumeData,
          [action.payload.page]:
            action.payload.data?.length > 0
              ? action?.payload?.data?.map((d) => d?.id)
              : [],
        };
      })
      .addCase(getAllResumes.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || action.error.message;
      })
      .addCase(getSingleResume.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getSingleResume.fulfilled, (state, action) => {
        state.isLoading = false;
        console.log("add single action", action.payload);
        resumeAdapter.setOne(state, action.payload);
      })
      .addCase(getSingleResume.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || action.error.message;
      })
      .addCase(rejectResume.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(rejectResume.fulfilled, (state, action) => {
        state.isLoading = false;
        const formatedData = action.payload.map((c) => ({
          id: c?.id,
          changes: c,
        }));
        resumeAdapter.updateMany(state, formatedData);
      })
      .addCase(rejectResume.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || action.error.message;
      })
      .addCase(deleteResume.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteResume.fulfilled, (state, action) => {
        state.isLoading = false;
        console.log(action.payload);
        if (action?.payload.page) {
          state.resumeData = {
            ...state.resumeData,
            [action.payload.page]: state.resumeData[
              action.payload.page
            ]?.filter((f) => f !== action.payload.ids[0]),
          };
        }
        resumeAdapter.removeMany(state, action.payload.ids);
      })
      .addCase(deleteResume.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || action.error.message;
      })
      .addCase(inviteCandidatesForInterview.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(inviteCandidatesForInterview.fulfilled, (state) => {
        state.isLoading = false;
        // const formatedData = action.payload.map((c) => ({ id: c?.id, changes: c }))
        // resumeAdapter.updateMany(state, formatedData)
      })
      .addCase(inviteCandidatesForInterview.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || action.error.message;
      })

      .addCase(uploadPdfsInBulk.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(uploadPdfsInBulk.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(uploadPdfsInBulk.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || action.error.message;
      })
      .addCase(uploadCsvInBulk.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(uploadCsvInBulk.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(uploadCsvInBulk.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || action.error.message;
      });
  },
});

// ACTION EXORT
// export const { clearPostCreationStates, editPostContent, clearPostFormData, updatePostFormData } = resumeSlice.actions;

// SELECTORS
export const {
  selectAll: selectAllResumes,
  selectById: selectResumeById,
  selectIds: selectResumeIds,
} = resumeAdapter.getSelectors((state) => state.resume);

export const selectLoading = (state) => state.resume.isLoading;
export const selectError = (state) => state.resume.error;

export default resumeSlice.reducer;
